import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { track, useTrackPageView } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import BookCover from "@/components/books/BookCover";
import BuyButton from "@/components/books/BuyButton";
import PrintInterestButton from "@/components/books/PrintInterestButton";
import StickyBuyBar from "@/components/books/StickyBuyBar";
import PriceBlock from "@/components/books/PriceBlock";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  CYBER_BOOKS,
  CYBER_BUNDLE,
  CYBER_SAFETY_FAQ,
  CYBER_SAFETY_ACCENT as C,
  findCyberBook,
} from "@/data/cyberSafetyBooks";

export default function CyberSafetyProduct() {
  const { bookSlug } = useParams();
  const book = findCyberBook(bookSlug);

  useTrackPageView(book ? `cyber_book${book.book_number}_view` : "cyber_book_view_unknown", { slug: bookSlug });
  useSEO({
    title: book ? `${book.title} — LifeIQ Cyber Fraud & Scam Safety, ${book.label}` : "LifeIQ Cyber Fraud & Scam Safety",
    description: book ? book.short_description : "LifeIQ Cyber Fraud & Scam Safety — a 3-book family digital safety series.",
    canonicalPath: `/books/cyber-safety/${bookSlug}`,
  });

  if (!book) {
    return <Navigate to="/books/cyber-safety" replace />;
  }

  const otherBooks = CYBER_BOOKS.filter((b) => b.slug !== book.slug);

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ backgroundColor: C.navy }}>
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-16 md:pt-20 pb-20 md:pb-28 relative">
          <HeroFade delay={0}>
            <div className="flex items-center gap-2 text-[12px] text-white/50 flex-wrap">
              <Link to="/books/cyber-safety" className="hover:text-white transition-colors">Cyber Fraud & Scam Safety</Link>
              <span>/</span>
              <span className="text-white/80">{book.title}</span>
            </div>
          </HeroFade>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 max-w-[320px]">
              <HeroFade delay={0.1}>
                <BookCover book={book} tilt testId="pdp-cover" />
              </HeroFade>
            </div>
            <div className="lg:col-span-8">
              <HeroFade delay={0.15}>
                <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>{book.label}</div>
              </HeroFade>
              <KineticTitle
                testId="pdp-title"
                className="mt-3 font-serif-d text-[32px] sm:text-[44px] leading-[1.08] text-white"
                lines={[book.title]}
              />
              <HeroFade delay={0.25}>
                <div className="mt-3 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.1em] px-3 py-1.5 rounded-sm" style={{ color: C.teal, backgroundColor: `${C.teal}22` }}>
                  {book.progression_label}
                </div>
                <p className="mt-4 text-[16px] sm:text-[17px] text-white/80 leading-[1.7] max-w-2xl">{book.short_description}</p>
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-white/55">
                  <span>{book.pages} pages</span>
                  <span>{book.language}</span>
                  <span>{book.format}</span>
                  <span>Personal / Family Use</span>
                </div>
              </HeroFade>
              <HeroFade delay={0.35}>
                <div className="mt-8">
                  <PriceBlock product={book} dark big testId="pdp-price-block" accentColor={C.gold} offerChipColor={C.gold} />
                </div>
                <p data-testid="pdp-buy-context" className="mt-3 text-[12px] uppercase tracking-[0.14em] text-white/55 font-semibold">
                  {book.language} · Digital Edition · PDF · Instant protected download
                </p>
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <BuyButton
                    product={book}
                    label={`Buy Digital Book — ${book.price}`}
                    source="product-page-hero"
                    testId="book-buy-button"
                    dark
                    accentColor={C.gold}
                  />
                  <a
                    href="#look-inside"
                    data-testid="pdp-look-inside-cta"
                    className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-6 py-4 rounded-sm text-[13px] font-semibold uppercase tracking-wide hover:bg-white/10 transition-colors duration-200"
                  >
                    Look Inside <ArrowRight size={14} />
                  </a>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3 text-[13px]">
                  <span className="text-white/40 uppercase tracking-wide text-[11px] font-semibold">Printed Edition · Planned</span>
                  <PrintInterestButton
                    mode="single"
                    seriesId="cyber-safety"
                    productId={book.product_id}
                    bookTitle={book.title}
                    sourcePage={`cyber-safety-pdp-${book.slug}`}
                    testId="pdp-print-interest"
                    accentColor={C.gold}
                    className="text-white/70 hover:text-white underline underline-offset-2 text-[13px] font-semibold transition-colors"
                  />
                </div>
              </HeroFade>
            </div>
          </div>
        </div>
      </section>

      {/* LOOK INSIDE */}
      <section id="look-inside" style={{ backgroundColor: C.cream }}>
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>Look inside</div>
            <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1]" style={{ color: C.navy }}>A preview of real pages.</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="pdp-look-inside-gallery">
            {book.preview_assets.map((src, i) => (
              <Reveal key={src} delay={i * 0.04}>
                <img
                  src={src}
                  alt={`${book.title} — preview page ${i + 1}`}
                  data-testid={`pdp-preview-${i}`}
                  className="w-full rounded-sm shadow-md border"
                  style={{ borderColor: `${C.navy}14` }}
                  loading="lazy"
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE — chapters */}
      <section className="bg-white">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.teal }}>What's inside</div>
            <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1]" style={{ color: C.navy }}>{book.chapters.length} sections, {book.pages} pages.</h2>
          </Reveal>
          <div className="mt-10 space-y-4" data-testid="pdp-chapter-list">
            {book.chapters.map((ch, i) => (
              <Reveal key={ch.name} delay={i * 0.03}>
                <div className="flex items-start gap-3 py-3 border-b" style={{ borderColor: `${C.navy}12` }}>
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0" style={{ color: C.teal }} />
                  <div>
                    <div className="text-[15px] font-semibold" style={{ color: C.navy }}>{ch.name}</div>
                    <div className="text-[14px]" style={{ color: C.textMuted }}>{ch.desc}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ backgroundColor: C.cream }}>
        <div className="max-w-[820px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>Questions</div>
            <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1]" style={{ color: C.navy }}>Common questions.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10 border-t" style={{ borderColor: `${C.navy}1A` }}>
              {CYBER_SAFETY_FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} style={{ borderColor: `${C.navy}1A` }}>
                  <AccordionTrigger className="font-serif-d text-[17px] hover:no-underline py-4" style={{ color: C.navy }}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[14px] leading-[1.65]" style={{ color: C.textMuted }}>{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CONTINUE EXPLORING */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.teal }}>Continue exploring</div>
            <h2 className="mt-4 font-serif-d text-[26px] sm:text-3xl leading-[1.1]" style={{ color: C.navy }}>The rest of the series.</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {otherBooks.map((b) => (
              <Link key={b.slug} to={`/books/cyber-safety/${b.slug}`} onClick={() => track("cyber_book_view", { product_id: b.product_id, source: "continue-exploring" })}>
                <BookCover book={b} className="max-w-[160px] mx-auto" />
                <div className="mt-3 text-center text-[13px] font-semibold" style={{ color: C.navy }}>{b.title}</div>
              </Link>
            ))}
            <Link to="/books/cyber-safety/complete-series" onClick={() => track("cyber_bundle_view", { source: "continue-exploring" })} className="flex flex-col items-center justify-center border rounded-sm p-6 text-center" style={{ borderColor: `${C.gold}55` }}>
              <div className="font-serif-d text-[18px]" style={{ color: C.navy }}>Complete Series</div>
              <div className="mt-1 text-[13px] font-bold" style={{ color: C.gold }}>{CYBER_BUNDLE.price}</div>
              <div className="mt-2 text-[12px] uppercase tracking-wide font-semibold flex items-center gap-1" style={{ color: C.teal }}>See all 3 <ArrowRight size={12} /></div>
            </Link>
          </div>
        </div>
      </section>

      <StickyBuyBar
        product={book}
        label="Buy Now"
        source="product-page-sticky"
        testId="pdp-sticky-buy"
        accentColor={C.gold}
        barClassName="border-[#2A3C60]"
      />
    </main>
  );
}
